/**
 * AlertListCards — the alert list as a GALLERY: one card per alert, the alert's own frame
 * filling the top of it.
 *
 * The same page of alerts AlertListTable draws, and deliberately the same props, so the list
 * page swaps between the two without threading anything new through. What differs is what
 * each is good at, and it is worth being explicit about:
 *
 *   • The TABLE is for working a queue — twenty-five rows on a screen, aligned columns, the
 *     clock and the assignee scannable straight down.
 *   • The CARDS are for recognising one — the picture is the whole point, and an operator
 *     asked "did we catch the guy at the loading bay" finds it here in a way no row of text
 *     supports.
 *
 * THE CARD IS READ TOP-DOWN, in the order a person triages. A severity rail across the top
 * edge (the card's version of the table's left stripe — colour at a fixed position, scannable
 * across a grid). Then the frame, with severity, time, what tripped and where laid over it.
 * Then the model's own sentence about it. Then where the alert stands and who has it. The
 * actions come last, on their own tinted foot, because acting is what you do AFTER reading —
 * and putting them there keeps them off the picture, which the operator is looking at.
 *
 * Terminal alerts (resolved / cancelled) fade and keep one control, Reopen. They're history:
 * a finished alert wearing four live buttons is an invitation to misclick on somebody else's
 * completed work.
 */
import {
  ArrowRightIcon,
  ArrowUturnLeftIcon,
  BellAlertIcon,
  BellSlashIcon,
  CheckCircleIcon,
  CheckIcon,
  PhotoIcon,
  UserPlusIcon,
  VideoCameraIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { AlertStatusTag, alertSeverityVar } from './AlertTags';
import { Avatar } from './Avatar';
import { alertName, whenStamp } from './alertVocab';
import { useAlertImage } from './useAlertImage';
import type { AlertListTableProps } from './AlertListTable';

/** The two views take the same page of data and the same callbacks — see the file header. */
export type AlertListCardsProps = AlertListTableProps;

function isTerminal(a: any) {
  return a?.status === 'resolved' || a?.status === 'cancelled';
}

function ActionBtn({ title, onClick, className = '', children }: any) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      className={`flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground ${className}`}
    >
      {children}
    </button>
  );
}

function AlertCardMedia({
  alert,
  name,
  camera,
  thumbnailFor,
  loadThumbnail,
}: {
  alert: any;
  name: string;
  camera: string;
  thumbnailFor?: (alert: any) => string | null;
  loadThumbnail?: (alert: any) => Promise<string | null>;
}) {
  const { src, markFailed, ref } = useAlertImage(alert, thumbnailFor, loadThumbnail);
  const sev = alertSeverityVar(alert?.severity);
  const severity = String(alert?.severity || 'normal');

  return (
    <div ref={ref} className="relative aspect-video w-full overflow-hidden bg-neutral-900">
      {/* The dark ground is painted whether or not a picture lands, so the chips and the
          white type over it never flip contrast when one does. */}
      <div className="absolute inset-0 bg-gradient-to-br from-neutral-800 to-neutral-950" />
      {src ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
          onError={markFailed}
        />
      ) : (
        <PhotoIcon className="absolute left-1/2 top-1/2 h-7 w-7 -translate-x-1/2 -translate-y-1/2 text-white/20" />
      )}
      {/* Bottom scrim only: a full overlay would grey the frame the operator came to look at. */}
      <div className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-black/85 via-black/45 to-transparent" />

      <div className="absolute inset-x-0 top-0 flex items-start justify-between gap-2 p-2.5">
        <span className="flex items-center gap-2">
          {/* Severity as a filled tile — over video, a tint doesn't survive a bright frame. */}
          <span
            className="flex h-7 w-7 items-center justify-center rounded-lg text-white"
            style={{ backgroundColor: sev }}
          >
            <BellAlertIcon className="h-4 w-4" />
          </span>
          <span className="text-[11px] font-bold uppercase tracking-wider text-white">{severity}</span>
        </span>
        <span className="rounded-md bg-black/60 px-2 py-1 text-[11px] font-medium tabular-nums text-white">
          {whenStamp(alert?.event_timestamp)}
        </span>
      </div>

      <div className="absolute inset-x-0 bottom-0 p-3">
        <p className="truncate text-[15px] font-semibold leading-tight text-white" title={name}>
          {name}
        </p>
        <p className="mt-1 flex items-center gap-1.5 text-[12px] text-white/80">
          <VideoCameraIcon className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate" title={camera}>{camera || '—'}</span>
        </p>
      </div>
    </div>
  );
}

export default function AlertListCards({
  alerts,
  loading,
  cameraMap = {},
  userMap = {},
  canManage = false,
  thumbnailFor,
  loadThumbnail,
  onOpen,
  onAcknowledge,
  onAssign,
  onResolve,
  onCancel,
  onReopen,
  emptyHint,
}: AlertListCardsProps) {
  /** Fills whatever width it is given — three up on a wide monitor, one on a phone — rather
   *  than a fixed column count that leaves a laptop with a stripe of empty page. */
  const GRID = 'grid gap-4 p-4 [grid-template-columns:repeat(auto-fill,minmax(19rem,1fr))]';

  if (loading) {
    return (
      <div className={GRID}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="h-72 animate-pulse rounded-xl bg-muted" />
        ))}
      </div>
    );
  }

  if (!alerts.length) {
    return (
      <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
        <BellSlashIcon className="h-8 w-8 text-muted-foreground opacity-40" />
        <p className="text-sm font-medium text-foreground">No alerts match these filters</p>
        {emptyHint && <p className="text-[13px] text-muted-foreground">{emptyHint}</p>}
      </div>
    );
  }

  return (
    <div className={GRID}>
      {alerts.map((a, idx) => {
        const terminal = isTerminal(a);
        const name = alertName(a);
        const camera = cameraMap[a.camera_id] || (a.camera_id ? String(a.camera_id).slice(0, 8) : '');
        const assignee = a.assigned_to ? userMap[a.assigned_to] || 'Assigned' : '';
        // Held even when empty so every card in a row is the same height and the grid keeps
        // its baselines — a card missing its sentence would otherwise ride up.
        const detail = a.summary || '—';

        return (
          <article
            key={a.id}
            className={`flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-card transition-shadow hover:shadow-card-hover ${
              terminal ? 'opacity-60' : ''
            }`}
          >
            {/* The card's severity rail: the table's left-edge stripe, turned along the top,
                so a grid is scannable by colour the same way the rows are. */}
            <span className="h-1 w-full shrink-0" style={{ backgroundColor: alertSeverityVar(a.severity) }} />

            {/* The frame opens the record. A button, not a div with onClick: this is the
                card's primary control and it has to be reachable from the keyboard. */}
            <button
              type="button"
              onClick={() => onOpen(idx)}
              aria-label={`Open ${name}`}
              className="block w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand"
            >
              <AlertCardMedia
                alert={a}
                name={name}
                camera={camera}
                thumbnailFor={thumbnailFor}
                loadThumbnail={loadThumbnail}
              />
            </button>

            <p
              className="truncate border-b border-border px-4 py-3 text-[13px] text-muted-foreground"
              title={a.summary || ''}
            >
              {detail}
            </p>

            <div className="flex items-center gap-3 px-4 py-3">
              <AlertStatusTag status={a.status} />
              {assignee ? (
                <span className="flex min-w-0 items-center gap-2">
                  <Avatar name={assignee} className="h-7 w-7 shrink-0 text-[10px]" />
                  <span className="truncate text-[13px] text-foreground" title={assignee}>{assignee}</span>
                </span>
              ) : (
                <span className="text-[13px] text-muted-foreground">Unassigned</span>
              )}
            </div>

            {/* `mt-auto` pins the foot to the bottom, so cards in a row line their controls up
                however long the sentence above them ran. */}
            <div className="mt-auto flex items-center justify-between gap-2 border-t border-border bg-muted/30 px-3 py-2">
              <div className="flex items-center gap-0.5">
                {!canManage ? (
                  <span className="text-[11px] text-muted-foreground">—</span>
                ) : terminal ? (
                  <ActionBtn title="Reopen alert" onClick={() => onReopen?.(a)}>
                    <ArrowUturnLeftIcon className="h-4 w-4" />
                  </ActionBtn>
                ) : (
                  <>
                    {a.status === 'new' && (
                      <ActionBtn title="Acknowledge" onClick={() => onAcknowledge?.(a)}>
                        <CheckIcon className="h-4 w-4" />
                      </ActionBtn>
                    )}
                    <ActionBtn title="Assign…" onClick={() => onAssign?.(a)}>
                      <UserPlusIcon className="h-4 w-4" />
                    </ActionBtn>
                    <ActionBtn title="Resolve" className="hover:text-emerald-500" onClick={() => onResolve?.(a)}>
                      <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
                    </ActionBtn>
                    <ActionBtn title="Cancel (false positive)" className="hover:text-red-500" onClick={() => onCancel?.(a)}>
                      <XCircleIcon className="h-4 w-4 text-red-500" />
                    </ActionBtn>
                  </>
                )}
              </div>

              {/* The frame already opens the record; this is the named way to the same place,
                  for the reader who doesn't know the picture is a button. */}
              <button
                type="button"
                onClick={() => onOpen(idx)}
                className="flex items-center gap-1 rounded-md px-2 py-1 text-[13px] font-semibold text-brand transition-colors hover:bg-muted"
              >
                Details
                <ArrowRightIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          </article>
        );
      })}
    </div>
  );
}
